import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, NavigationLeftIcon, Preloader, RawButton, Text, useResponsive } from '@common-fe/common-fe';

import { Modal } from '@/components';
import AppButton from '@/components/controls/AppButton';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import { useManageAccountAccessState } from './hooks/useManageAccountAccessState';
import { useGetCardPurses } from './queries/useGetCardPurses.query';
import { usePatchCardPurses } from './queries/usePatchCardPurses.query';
import { ManageAccountAccessForm } from './components';

interface Props {
  employeeId?: string | number;
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

export const ManageAccountAccessModal: React.FC<Props> = ({ employeeId, visible, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { purses, isLoading } = useGetCardPurses(employeeId);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const handlePatchError = useCallback(() => {
    handleAddPermanentSnackbar({
      text: t('Oops! Something went wrong, please try again'),
      closeIcon: true,
      state: SnackbarState.negative,
    });
  }, [handleAddPermanentSnackbar, t]);
  const { patchCardPurses, isPending } = usePatchCardPurses({ onError: handlePatchError });
  const { lockedIds, hasChanges, toggleLock, reset, buildPayload } = useManageAccountAccessState({ purses });

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  const handleSubmit = useCallback(async () => {
    if (hasChanges) {
      try {
        await patchCardPurses({
          cardPurses: buildPayload(),
          employeeId,
        });
      } catch {
        return;
      }
      onSuccess?.();
    }
    reset();
    onCancel();
  }, [hasChanges, patchCardPurses, buildPayload, employeeId, reset, onCancel, onSuccess]);

  const content = isLoading ? (
    <Box align="center" pad="spacing32">
      <Preloader />
    </Box>
  ) : (
    <ManageAccountAccessForm
      purses={purses}
      lockedIds={lockedIds}
      isLoading={isPending}
      onToggle={toggleLock}
      onCancel={handleCancel}
      onSave={handleSubmit}
    />
  );

  return (
    <Modal
      testId="manage-account-access"
      visible={visible}
      onSetVisible={handleCancel}
      isCloseButtonHidden={isMobile}
      isFullScreen={isMobile}
      contentMobileStyle={{ padding: 0 }}
    >
      {isMobile ? (
        <>
          <Box margin={{ vertical: 'spacing12' }}>
            <RawButton onClick={handleCancel} data-testid="manage-account-access_back-button">
              <Box direction="row" align="center" height="40px" pad={{ horizontal: 'spacing8' }}>
                <Box margin={{ left: 'spacing10' }}>
                  <NavigationLeftIcon color="textAccent" size="20px" />
                </Box>
                <Text color="textAccent" size="14px" weight={700} margin={{ left: 'spacing10' }}>
                  {t('Manage Account Access')}
                </Text>
              </Box>
            </RawButton>
          </Box>
          <Box
            pad={{ vertical: 'spacing24', horizontal: 'spacing16' }}
            margin={{ horizontal: '2px' }}
            background="module"
            round="container3Round"
            style={{ marginBottom: '160px' }}
          >
            {content}
          </Box>
          <Box
            background="canvas"
            pad={{ horizontal: 'spacing24', top: 'spacing12' }}
            gap="spacing12"
            style={{ position: 'absolute', bottom: '32px', width: '100%' }}
          >
            <AppButton
              disabled={isPending}
              type="button"
              width="100%"
              testId="manage-account-access-form_save-button"
              onClick={handleSubmit}
            >
              {isPending ? <Preloader color="white" /> : t('Save')}
            </AppButton>
            <AppButton
              buttonType="secondary"
              width="100%"
              type="button"
              testId="manage-account-access-form_cancel-button"
              onClick={handleCancel}
            >
              {t('Cancel')}
            </AppButton>
          </Box>
        </>
      ) : (
        <Box direction="column">
          <Box
            direction="row"
            align="center"
            justify="center"
            pad={{ bottom: 'spacing24', horizontal: 'spacing24' }}
          >
            <Text size="2xl" color="textBody" weight="bold" textAlign="center">
              {t('Manage Account Access')}
            </Text>
          </Box>
          {content}
        </Box>
      )}
    </Modal>
  );
};

export default ManageAccountAccessModal;
